import { Trans, useLingui } from "@lingui/react/macro";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { DataTableReactInstance } from "./table-features";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../base/pagination";

export function PaginationControl<T>({
  table,
}: {
  table: DataTableReactInstance<T>;
}) {
  const { t } = useLingui();
  const pageIndex = table.state.pagination.pageIndex;
  const pageCount = Math.max(table.getPageCount(), 1);
  const isFirst = pageIndex === 0;
  const isLast = pageIndex >= pageCount - 1;
  const currentPage = pageIndex + 1;

  const [inputValue, setInputValue] = useState(String(currentPage));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  const handleSubmit = () => {
    const page = parseInt(inputValue, 10);
    if (!isNaN(page) && page >= 1 && page <= pageCount) {
      table.setPageIndex(page - 1);
    } else {
      setInputValue(String(currentPage));
    }
    inputRef.current?.blur();
  };

  return (
    <Pagination className="mx-0 w-auto justify-start">
      <PaginationContent>
        <PaginationItem>
          <PaginationLink
            onClick={() => table.setPageIndex(0)}
            aria-disabled={isFirst}
            aria-label={t`Go to first page`}
            className={isFirst ? "pointer-events-none opacity-50" : ""}
          >
            <ChevronsLeftIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem>
          <PaginationLink
            onClick={() => table.previousPage()}
            aria-disabled={isFirst}
            aria-label={t`Go to previous page`}
            className={isFirst ? "pointer-events-none opacity-50" : ""}
          >
            <ChevronLeftIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem>
          <span className="flex items-baseline gap-1.5 px-2 text-sm">
            <Trans>
              Page
              <input
                ref={inputRef}
                type="number"
                min={1}
                max={pageCount}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onBlur={handleSubmit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  } else if (e.key === "Escape") {
                    setInputValue(String(currentPage));
                    inputRef.current?.blur();
                  }
                }}
                onFocus={(e) => e.target.select()}
                className="h-8 w-12 rounded-md border border-input bg-white dark:bg-input/30 px-2 text-center text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
              of {pageCount}
            </Trans>
          </span>
        </PaginationItem>

        <PaginationItem>
          <PaginationLink
            onClick={() => table.nextPage()}
            aria-disabled={isLast}
            aria-label={t`Go to next page`}
            className={isLast ? "pointer-events-none opacity-50" : ""}
          >
            <ChevronRightIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>

        <PaginationItem>
          <PaginationLink
            onClick={() => table.setPageIndex(pageCount - 1)}
            aria-disabled={isLast}
            aria-label={t`Go to last page`}
            className={isLast ? "pointer-events-none opacity-50" : ""}
          >
            <ChevronsRightIcon className="size-4" />
          </PaginationLink>
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}
